import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Languages, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { useUpsertLanguage, useWorkspaceLanguages } from '@/lib/api';
import { AddLanguageDialog } from '@/components/workspaces/AddLanguageDialog';

export function LanguagesTab() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const languages = useWorkspaceLanguages(wsId);
  const upsert = useUpsertLanguage();
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setAdding(true)}>
          <Plus aria-hidden />
          Add language
        </Button>
      </div>

      {languages.isLoading && <Spinner label="Loading languages…" />}

      {languages.isError && (
        <p
          role="alert"
          className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
        >
          Failed to load languages: {languages.error.message}
        </p>
      )}

      {languages.isSuccess && languages.data.length === 0 && (
        <EmptyState
          icon={Languages}
          title="No languages declared"
          description="Add the language codes your products translate into. Projects pick from this list."
          action={<Button onClick={() => setAdding(true)}>Add a language</Button>}
        />
      )}

      {languages.isSuccess && languages.data.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {languages.data.map((lang) => (
            <li
              key={lang.id}
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
            >
              <div>
                <p className="font-medium">{lang.label || lang.code}</p>
                <p className="text-xs text-muted-foreground">{lang.code}</p>
              </div>
              {lang.pluralForms && <Badge tone="outline">plural ✓</Badge>}
            </li>
          ))}
        </ul>
      )}

      <AddLanguageDialog
        open={adding}
        onClose={() => setAdding(false)}
        pending={upsert.isPending}
        existingCodes={languages.data?.map((l) => l.code) ?? []}
        onSubmit={async (input) => {
          try {
            await upsert.mutateAsync({ workspaceID: wsId, ...input });
            toast.show(`Language ${input.code} added`, 'success');
            setAdding(false);
          } catch (err) {
            toast.show((err as Error).message, 'error');
          }
        }}
      />
    </div>
  );
}
